import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { blank, OptionalValue } from '../../../../utils/common';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'fieldmagic-color-picker-input',
  template: `
    <ng-container *ngIf="! standalone">
      <div class="form-group">
        <div class="d-flex d-flex-gap">
          <fieldmagic-text
            purpose="input"
            [content]="label"
            [withRequiredMarker]="withRequiredMarker"
          >
          </fieldmagic-text>

          <fieldmagic-icon
            *ngIf="tooltip | filled"
            icon="info-circle"
            [tooltip]="tooltip"
          >
          </fieldmagic-icon>
        </div>
        <ng-container *ngTemplateOutlet="input"></ng-container>
      </div>
    </ng-container>

    <ng-container
      *ngIf="standalone"
      [ngTemplateOutlet]="input"
    >
    </ng-container>

    <ng-template #input>
      <div class="d-flex d-flex-gap align-items-center">
        <fa-icon
          *ngIf="! displayOnly"
          class="fa-2x"
          [icon]="['fas', 'circle']"
          [style.color]="value"
          [(colorPicker)]="value"
          cpPosition="bottom"
          [cpCancelButton]="true"
          (colorPickerChange)="onChanged($event)"
        >
        </fa-icon>

        <fa-icon
          *ngIf="displayOnly"
          class="fa-2x"
          [icon]="['fas', 'circle']"
          [style.color]="value"
        >
        </fa-icon>

        <span *ngIf="displayValue">{{ value }}</span>
      </div>

      <div
        fieldmagicGridColumn="12"
      >
        <fieldmagic-input-errors
          *ngIf="errors | filled"
          [errors]="errors">
        </fieldmagic-input-errors>
      </div>
    </ng-template>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorPickerInputComponent),
      multi: true,
    },
  ],
})
export class ColorPickerInputComponent implements ControlValueAccessor {
  @Input() errors: string | string[] = [];

  @Input() label?: string;

  @Input() tooltip?: string;

  @Input() standalone: boolean = false;

  @Input() withRequiredMarker: boolean = false;

  @Input() displayValue: boolean = true;

  @Input() displayOnly: boolean = false;

  @Input()
  set disabled(value: boolean) {
    this.isDisabled$.next(value);
  }

  @Output('change') $onChanged = new EventEmitter<FieldmagicColorInputOnChangedEvent>();

  value?: string;

  readonly isDisabled$ = new BehaviorSubject<boolean>(false);

  readonly fontColor$ = new BehaviorSubject<string>('#ffff');

  // Callback when input is touched
  onTouched = () => { };

  // Callback when input value was changed
  onChanged = (value: OptionalValue<string>) => this.$onChanged.next(value);

  /// calls to register onChanged handler
  registerOnChange = (fn: (value: OptionalValue<string>) => void) => this.onChanged = (value: OptionalValue<string>) => {
    fn(value);
    this.$onChanged.next(value);
    this._computeFontColor(value);
  };

  /// calls to register onTouched handler
  registerOnTouched = (fn: () => void) => this.onTouched = fn;

  setDisabledState = (state: boolean) => this.isDisabled$.next(state);

  /// called when value is being written
  writeValue = (value: OptionalValue<string>) => this.value = value;

  private _computeFontColor(hex: OptionalValue<string>): void {
    if (blank(hex)) {
      return;
    }

    hex = hex.replace('#', '');

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    this.fontColor$.next((brightness > 128) ? '#0000' : '#ffff');
  }
}

type FieldmagicColorInputOnChangedEvent = OptionalValue<string>;